/**
 * ============================================================================
 * COMPOSABLE: Предупреждение об истечении токена
 * ============================================================================
 * Отслеживает время жизни refresh token и предупреждает пользователя
 * за 5 минут до истечения
 * ============================================================================
 */

import { ref, onMounted, onUnmounted } from 'vue'
import { useAppStore } from '@/stores/useAppStore'

const WARNING_THRESHOLD = 5 * 60 * 1000 // 5 минут
const CHECK_INTERVAL = 60 * 1000 // Проверять каждую минуту
const CRITICAL_THRESHOLD = 2 * 60 * 1000 // 2 минуты - критическое предупреждение

export function useTokenExpirationWarning() {
	const appStore = useAppStore()
	const timeUntilExpiration = ref(null)
	const hasShownWarning = ref(false)
	const hasShownCriticalWarning = ref(false)
	let checkInterval = null

	/**
	 * Проверка времени до истечения refresh token
	 */
	function checkTokenExpiration() {
		const refreshToken = localStorage.getItem('refresh_token')
		
		if (!refreshToken) {
			timeUntilExpiration.value = null
			return
		}

		try {
			const payload = JSON.parse(atob(refreshToken.split('.')[1]))
			const expiresAt = payload.exp * 1000
			const now = Date.now()
			const timeLeft = expiresAt - now

			timeUntilExpiration.value = timeLeft

			// Критическое предупреждение (2 минуты)
			if (timeLeft > 0 && timeLeft < CRITICAL_THRESHOLD && !hasShownCriticalWarning.value) {
				hasShownCriticalWarning.value = true
				appStore.addNotification({
					type: 'error',
					title: 'Критическое предупреждение',
					message: 'Ваша сессия истекает через 2 минуты! Срочно сохраните работу.',
					duration: 6000,
					channel: 'toast',
				})
				console.warn('⚠️ [TokenExpiration] Критическое предупреждение: осталось', Math.round(timeLeft / 1000), 'секунд')
			}
			// Обычное предупреждение (5 минут)
			else if (timeLeft > 0 && timeLeft < WARNING_THRESHOLD && !hasShownWarning.value) {
				hasShownWarning.value = true
				appStore.addNotification({
					type: 'warning',
					title: 'Предупреждение о сессии',
					message: 'Ваша сессия истекает через 5 минут. Рекомендуем сохранить работу.',
					duration: 6000,
					channel: 'toast',
				})
				console.warn('⚠️ [TokenExpiration] Предупреждение: осталось', Math.round(timeLeft / 1000 / 60), 'минут')
			}
			// Токен истёк
			else if (timeLeft <= 0) {
				console.warn('⚠️ [TokenExpiration] Refresh token истёк')
				stopChecking()
			}
		} catch (error) {
			console.error('❌ [TokenExpiration] Ошибка декодирования токена:', error)
			timeUntilExpiration.value = null
		}
	}

	/**
	 * Запуск периодической проверки
	 */
	function startChecking() {
		if (checkInterval) return

		// Первая проверка сразу
		checkTokenExpiration()

		// Затем каждую минуту
		checkInterval = setInterval(checkTokenExpiration, CHECK_INTERVAL)
		console.log('✅ [TokenExpiration] Мониторинг запущен')
	}

	/**
	 * Остановка проверки
	 */
	function stopChecking() {
		if (checkInterval) {
			clearInterval(checkInterval)
			checkInterval = null
			console.log('🛑 [TokenExpiration] Мониторинг остановлен')
		}
	}

	/**
	 * Сброс флагов предупреждений (при обновлении токена)
	 */
	function resetWarnings() {
		hasShownWarning.value = false
		hasShownCriticalWarning.value = false
		console.log('🔄 [TokenExpiration] Флаги предупреждений сброшены')
	}

	// Слушаем событие обновления токена
	function handleTokenRefresh() {
		resetWarnings()
		checkTokenExpiration()
	}

	onMounted(() => {
		startChecking()
		window.addEventListener('auth:token-refreshed', handleTokenRefresh)
	})

	onUnmounted(() => {
		stopChecking()
		window.removeEventListener('auth:token-refreshed', handleTokenRefresh)
	})

	return {
		timeUntilExpiration,
		startChecking,
		stopChecking,
		resetWarnings,
		checkTokenExpiration,
	}
}
